// Card-state primitives, extracted from GameBoard.jsx. These are the
// single funnels for atomic card mutations (see CLAUDE.md / memory):
// applyTap for every card.tapped flip, applyControllerFlip for every
// control change, flipCard for face flips, moveCardToCell for forced /
// effect movement, toggleCardStatus + rebuildStatusBadges +
// syncCardEffects for status visuals, the life/movement HUD attach +
// refresh helpers, payCastingCost, and finalizeFreshPlayCommit (the
// per-card recipe a fresh play runs on drop). New callers must route
// through the GameBoard delegates, never reimplement these recipes.
//
// Rule semantics stay in engine/core (tap.js, controlChange.js,
// casting.js, summoningSickness.js); this module wires them to meshes,
// HUD sprites, broadcasts, and logs.

import { animateCardFlip, animateCardTap } from '../../../utils/game/animations';
import { STATUS_EFFECTS, TOKEN_CARD_SCALE, buildStatusBadges, createCounterHUD, createLifeHUD, updateCounterHUD, updateCounterHUDOrientation, updateLifeHUD, updateLifeHUDOrientation } from '../../../utils/game/cardMesh';
import { REGION, TRIGGER, applyCastAsSpiritTransform, applyStatusToggle, cardUsesCounters, currentRegion as currentRegionForce, getEffectiveStatuses, landingRegionForMove, loseWardIfDisabledOrSilenced, regionConsequence, shouldCastAsSpirit } from '../../../utils/game/engine/automation';
import { resolveAvatarRules } from '../../../utils/game/avatarRules';
import { displayStatusKeys } from './equipActions';
import { applyLateCharge, canForceBurrowAll, hasSummoningSickness, canForceSubmergeAll, countsAsMinion, countsAsUnit, effectiveMovementBonus, getCardCost, isArtifact, loseStealthIfDisabledOrSilenced, markSummonedThisTurn, nextTapToggle, reconcileControlChange, regionsAtCell, sampleCellGeometry, survivesForcedBury, survivesForcedSubmerge, turnKey, unitFootprintCells } from '../../../utils/game/engine/core';
import { syncCardFace } from '../../../utils/game/engine/core/cardFace';
import { getCellNumber } from '../../../utils/game/grid';
import { moveKinematicBody } from '../../../utils/game/physicsWorld';
import { playSound } from '../../../utils/game/sounds';
import { getGameGrid } from '../../../utils/game/spawnConfig';
import { CARD_STATUS_EFFECTS } from '../../../utils/game/visualEffects';
import { toast } from 'sonner';
import { t, k } from '../../../localization/t';
import { msg } from '../../../utils/game/logMessage';
import { blocksCostTap, canTap } from '../../../utils/game/engine/core';
import { setCardBodyLandscape } from '../../../utils/game/physicsWorld';
import { cardLogLabel } from './logbookChat';
import { FIRST_UNIT_TUTORIAL_KEY } from './eventTutorials';

// v1.57. World-space center of a grid cell. Used to position the
// defender mesh when committing a Move-and-Defend.
// v1.79. Resolve the source/caster's cell for forced-movement ops.
// For spells, the cast pivot stored a casterOverrides.sourcePos; for
// activated abilities, fall back to the source card's mesh position.
export function resolveSourceCell(host, pt) {
  if (pt?.casterOverrides?.sourcePos) {
    return host._cellAtWorld(pt.casterOverrides.sourcePos.x, pt.casterOverrides.sourcePos.z);
  }
  const mesh = host.meshes.get(pt?.sourceCard?.id);
  if (!mesh) return null;
  return host._cellAtWorld(mesh.position.x, mesh.position.z);
}

// v1.79 / v1.87. Move a card to a destination cell with region
// rules enforced. moveType is 'forced' (PUSH / PULL. Codex: cannot
// cross regions) or 'teleport' (codex: can cross regions). Returns
// true if the move landed, false if it was rejected / fizzled.
//
// Codex consequences applied AFTER the move lands:
//   • void + no Voidwalk → banish (Banished pile)
//   • underwater + no Submerge → die (Cemetery)
//   • underground + no Burrowing → die (Cemetery)
//   • Avatar in any non-surface region → move rejected entirely
//     (Avatars may never enter those regions per codex)
//
// Forced moves that can't preserve the source region also fizzle
// ("forced movement cannot cause a card to change regions").
// The single low-level move primitive: write the mesh + physics body +
// card.x/z, then broadcast the new pose so the opponent's board matches.
// Reuses the canonical card:release transport. The only sync path that
// updates body pose AND cardInstance.x/z + re-resolves _auraAnchor on
// receipt. Sends the BODY quaternion: the receiver re-composes the lay-flat
// intent (mesh.quaternion = body x intent), so shipping mesh.quaternion would
// double-apply it and the card would land skewed. `extras` carries state the
// standard mesh broadcast can't (e.g. aura _visitedCells).
export function commitCardMove(host, card, worldPos, { extras = null } = {}) {
  if (!card || !worldPos) return false;
  const mesh = host.meshes.get(card.id);
  if (!mesh) return false;
  mesh.position.x = worldPos.x;
  mesh.position.z = worldPos.z;
  card.x = worldPos.x;
  card.z = worldPos.z;
  const body = mesh.userData?.body;
  if (body) moveKinematicBody(body, worldPos.x, mesh.position.y, worldPos.z);
  const quat = body ? body.quaternion : mesh.quaternion;
  host._presenceRelease(card.id, [worldPos.x, mesh.position.y, worldPos.z], [quat.x, quat.y, quat.z, quat.w], extras);
  return true;
}

export function moveCardToCell(host, card, cell, sourceLabel, verb, moveType = 'forced') {
  if (!card || !cell) return false;
  const mesh = host.meshes.get(card.id);
  if (!mesh) return false;
  const getFullCard = host._getFullCard;
  const fromCellPos = host._cellAtWorld(mesh.position.x, mesh.position.z);
  const fromCellLabel = host._cellNumberAt(mesh.position.x, mesh.position.z);
  // Source region: if the cell has no site under this card, the
  // unit is currently in the void (only legal for Voidwalk units).
  const fromRegions = fromCellPos
    ? regionsAtCell(host.meshes, host._cellAtWorld, fromCellPos.col, fromCellPos.row, getFullCard)
    : new Set([REGION.SURFACE]);
  const sourceRegion = fromRegions.has(REGION.VOID) && !fromRegions.has(REGION.SURFACE)
    ? REGION.VOID
    : currentRegionForce(card);
  const destRegions = regionsAtCell(host.meshes, host._cellAtWorld, cell.col, cell.row, getFullCard);
  const landing = landingRegionForMove(card, sourceRegion, destRegions, { moveType });
  if (!landing) {
    // Forced move that can't preserve the region (codex). Fizzle
    // silently with a log line so the player sees why nothing
    // happened.
    host.logAction("{source}: {card} can't {verb} (region barrier)", { source: sourceLabel, card: cardLogLabel(card, msg('unit')), verb: msg(verb) });
    return false;
  }
  const consequence = regionConsequence(card, landing);
  if (consequence === 'fail') {
    // Avatar would land somewhere it may never enter. Reject.
    host.logAction('{source}: {card} cannot enter that region', { source: sourceLabel, card: cardLogLabel(card, msg('unit')) });
    return false;
  }
  // Commit the position write + subsurface field. Subsurface tracks
  // the dest region for underground/underwater landings so future
  // gating (burrowSubmerge.js) sees the same source-of-truth. commitCardMove
  // also broadcasts the new pose. Forced/ability moves used to update only
  // the acting player's screen (Persecutor / teleport / pull / push desync).
  const worldPos = host._cellCenterWorld(cell.col, cell.row);
  if (!worldPos) return false;
  commitCardMove(host, card, worldPos);
  if (landing === REGION.UNDERGROUND || landing === REGION.UNDERWATER) {
    card.subsurface = landing;
  } else {
    card.subsurface = null;
  }
  const toCellLabel = getCellNumber(cell);
  host.logAction('{source}: {verb} {card} from [{from}] to [{to}]', { source: sourceLabel, verb: msg(verb), card: cardLogLabel(card, msg('unit')), from: fromCellLabel, to: toCellLabel });
  // Apply codex consequences (banish or send to cemetery) BEFORE
  // recomputing auras so leave-play effects fire in the right order.
  if (consequence === 'banish') {
    host.logAction('{card} is banished (void without Voidwalk)', { card: cardLogLabel(card, msg('unit')) });
    host.sendCardToPile(card, 'Banished');
  } else if (consequence === 'die') {
    const unit = cardLogLabel(card, msg('unit'));
    if (landing === REGION.UNDERWATER) host.logAction('{card} drowns (no Submerge)', { card: unit });
    else host.logAction('{card} suffocates (no Burrowing)', { card: unit });
    host.sendCardToPile(card, 'Cemetery');
  } else {
    // Fire on_enter_location triggers BEFORE the recompute below.
    // The recompute rebuilds `_grantedTriggers` from scratch. We
    // need the still-installed grants from the previous pass so
    // Druid's site-granted damage trigger fires for the unit that
    // just stepped onto the granted site.
    host._fireOnEnterLocationAt(card, cell);
  }
  host.recomputeStaticAuras();
  return true;
}

// Direct placement with NO movement semantics. No enter-location
// triggers, no region barriers, no codex consequences. Kairos'
// restore uses this ("It's like we're rewinding time, not actually
// moving cards around"): a rewound card never "enters" its cell, and
// an Avatar may legitimately end up in the void this way. Any
// consequence for cards left in impossible regions is the CALLER's
// job (Kairos runs one void sweep after every return has landed).
export function placeCardAtCell(host, card, cell, sourceLabel, verb = k('returns')) {
  if (!card || !cell) return false;
  const mesh = host.meshes.get(card.id);
  if (!mesh) return false;
  const worldPos = host._cellCenterWorld(cell.col, cell.row);
  if (!worldPos) return false;
  const fromCellLabel = host._cellNumberAt(mesh.position.x, mesh.position.z);
  mesh.position.x = worldPos.x;
  mesh.position.z = worldPos.z;
  if (mesh.userData?.body) {
    moveKinematicBody(mesh.userData.body, worldPos.x, mesh.position.y, worldPos.z);
  }
  host.logAction('{source}: {verb} {card} from [{from}] to [{to}]', { source: sourceLabel, verb: msg(verb), card: cardLogLabel(card, msg('unit')), from: fromCellLabel, to: getCellNumber(cell) });
  return true;
}

export function cellCenterWorld(host, col, row) {
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return null;
  const sample = sampleCellGeometry(grid, col, row);
  if (!sample) return null;
  return {
    x: (sample.tl.x + sample.br.x) / 2,
    z: (sample.tl.z + sample.br.z) / 2,
  };
}

// Small helper: re-render a card's life HUD after a damage write.
export function refreshLifeHUD(host, card) {
  const hud = host.lifeHUDs.get(card.id);
  if (!hud) return;
  const display = (card.currentLife || 0) + (card._auraStatDelta || 0);
  updateLifeHUD(hud.hpSprite, display, 'hp', display - (card.baseLife ?? display));
}

// The card supplying the level count shown on `anchor`'s mesh: the anchor
// itself if it tracks counters, otherwise the first equipped card that
// does. The Immortal Throne equipped to a site has no mesh of its own, so
// its bearer surfaces the badge. Otherwise the count would be invisible.
function counterSourceFor(host, anchor) {
  if (cardUsesCounters(host._getFullCard(anchor.cardId))) return anchor;
  for (const eq of (anchor.equippedCards || [])) {
    if (cardUsesCounters(host._getFullCard(eq.cardId))) return eq;
  }
  return null;
}

// Create / update / re-orient the centred level-counter token on a card's
// mesh. The count comes from the card itself or, if it carries an equipped
// counter card (The Immortal Throne as a Relic), from that. Idempotent:
// updates in place, attaches once, removes when no counter source remains.
export function refreshCounterHUD(host, anchorCardId) {
  const mesh = host.meshes.get(anchorCardId);
  if (!mesh) return;
  const anchor = mesh.userData?.cardInstance;
  if (!anchor) return;
  const src = counterSourceFor(host, anchor);
  const existing = host.counterHUDs.get(anchorCardId);
  if (!src) {
    if (existing) {
      mesh.remove(existing);
      existing.material.map?.dispose();
      existing.material.dispose();
      host.counterHUDs.delete(anchorCardId);
    }
    return;
  }
  if (existing) {
    updateCounterHUD(existing, src._counters || 0);
    updateCounterHUDOrientation(existing, anchor, host.state.isHost);
    return;
  }
  const hud = createCounterHUD(anchor, host.state.isHost);
  updateCounterHUD(hud, src._counters || 0);
  mesh.add(hud);
  host.counterHUDs.set(anchorCardId, hud);
}

// Refresh the token for a counter card by its OWN id, whether it sits on
// the field (own mesh) or is equipped (its bearer's mesh shows the badge).
// The counter-bump, network, and equip paths only know the counter card's
// id, not which mesh currently displays it.
export function refreshCounterAnchorFor(host, counterCardId) {
  if (host.meshes.has(counterCardId)) {
    refreshCounterHUD(host, counterCardId);
    return;
  }
  for (const [id, mesh] of host.meshes) {
    if (mesh?.userData?.cardInstance?.equippedCards?.some((c) => c.id === counterCardId)) {
      refreshCounterHUD(host, id);
      return;
    }
  }
}

// Attach an ATK+HP LifeHUD to a card mid-life. Used by Enchantress'
// animate-aura transform: addCardToTable only spawns a HUD for cards
// that enter the realm as Minions, so a card that BECOMES a minion
// later needs the HUD stamped retroactively. No-op when the card
// already carries a HUD (idempotent).
export function attachLifeHUDIfMissing(host, card) {
  if (!card) return;
  if (host.lifeHUDs.has(card.id)) return;
  const mesh = host.meshes.get(card.id);
  if (!mesh) return;
  const hud = createLifeHUD(card, host.state.isHost);
  mesh.add(hud.sprite);
  mesh.add(hud.hpSprite);
  if (hud.movSprite) mesh.add(hud.movSprite);
  host.lifeHUDs.set(card.id, hud);
}

// Inverse of _attachLifeHUDIfMissing. Remove the HUD from the mesh
// and the lookup map. Used by Enchantress' revert path so a card
// that's back to its printed Aura shape stops displaying ATK/HP
// pills. No-op when the card has no HUD.
export function detachLifeHUD(host, card) {
  if (!card) return;
  const hud = host.lifeHUDs.get(card.id);
  if (!hud) return;
  const mesh = host.meshes.get(card.id);
  if (mesh) {
    if (hud.sprite)    mesh.remove(hud.sprite);
    if (hud.hpSprite)  mesh.remove(hud.hpSprite);
    if (hud.movSprite) mesh.remove(hud.movSprite);
  }
  host.lifeHUDs.delete(card.id);
}

// Repaint the movement +/- chip from the card's current `_movementBonus`.
// The chip auto-hides when the bonus returns to 0 (handled inside
// updateLifeHUD for the 'mov' stat). Cheap enough to call from every
// mutation site so the chip always reflects the live engine value.
export function refreshMovementHUD(host, cardId) {
  const hud = host.lifeHUDs.get(cardId);
  if (!hud?.movSprite) return;
  const mesh = host.meshes.get(cardId);
  // Effective bonus: silence / disable suppress Movement +N, so the chip
  // hides (updateLifeHUD auto-hides the 'mov' chip at 0) and reappears
  // when the state clears. Matching the engine's reach computation.
  const bonus = effectiveMovementBonus(mesh?.userData?.cardInstance);
  updateLifeHUD(hud.movSprite, bonus, 'mov', bonus);
}

// v1.51. Spawn a floating damage number above a target mesh.
// Position captured once at spawn time (viewport pixels projected
// from the mesh's top-center); the floater animates via CSS
// transform from there, no per-frame re-projection needed.
// Small random horizontal jitter avoids stacking when both sides
// take damage on the same strike.
// World-point core shared by the mesh-anchored and position-anchored
// spawns. The combat director needs the position form because a killed
// card's mesh is already swept to the cemetery by the time its impact
// beat plays. The number rises from where the card died.
export function spawnFloatingAtWorld(host, worldPos, { amount = 0, label = null, color = '#ef4444' } = {}) {
  if (!worldPos || (!label && !(amount > 0))) return;
  if (!host.scene?.camera || !host.canvasRef.current) return;
  const camera = host.scene.camera;
  const rect = host.canvasRef.current.getBoundingClientRect();
  const v = host._hugVec;
  v.set(worldPos.x, worldPos.y, worldPos.z).project(camera);
  // Canvas-local pixel coords. See the note in the mesh variant below.
  const x = ((v.x + 1) / 2) * rect.width;
  const y = ((-v.y + 1) / 2) * rect.height;
  const jitterPx = (Math.random() - 0.5) * 24;
  const id = `dmg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  host.setState((s) => ({
    floatingDamage: [...(s.floatingDamage || []), {
      id, x: x + jitterPx, y, value: amount, label, color,
    }],
  }));
}

// Top-center world point of a card mesh. The floater anchor.
export function floaterAnchorForMesh(host, targetMesh) {
  if (!targetMesh) return null;
  const box = host._hugBox.setFromObject(targetMesh);
  if (!isFinite(box.min.x) || box.isEmpty()) return null;
  return {
    x: (box.min.x + box.max.x) / 2,
    y: box.max.y,
    z: (box.min.z + box.max.z) / 2,
  };
}

// Short text feedback over a card ("WARDED", "0"). Same float/fade as
// the damage numbers so every resolution produces SOME visible beat.
export function spawnFloatingText(host, targetMesh, label, color = '#7ec8ff') {
  const anchor = floaterAnchorForMesh(host, targetMesh);
  if (anchor) spawnFloatingAtWorld(host, anchor, { label, color });
}

export function spawnFloatingDamage(host, targetMesh, amount, color) {
  if (!targetMesh || !amount || amount <= 0) return;
  if (!host.scene?.camera || !host.canvasRef.current) return;
  const camera = host.scene.camera;
  const rect = host.canvasRef.current.getBoundingClientRect();
  const box = host._hugBox.setFromObject(targetMesh);
  if (!isFinite(box.min.x) || box.isEmpty()) return;
  const v = host._hugVec;
  const cx = (box.min.x + box.max.x) / 2;
  const ty = box.max.y;
  const cz = (box.min.z + box.max.z) / 2;
  v.set(cx, ty, cz).project(camera);
  // Canvas-local pixel coords. FloatingDamageNumber renders
  // position:fixed inside .app-canvas (the containing block via its
  // translateZ(0)), so left/top are measured from the CANVAS origin.
  // Adding rect.left/top here shifted every floater by the letterbox
  // offset whenever the window wasn't exactly 16:9. Far enough to
  // push them off-screen entirely. Same convention as
  // updateEquipPendingOverlays and the other projection passes.
  const x = ((v.x + 1) / 2) * rect.width;
  const y = ((-v.y + 1) / 2) * rect.height;
  const jitterPx = (Math.random() - 0.5) * 24;
  const id = `dmg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  host.setState((s) => ({
    floatingDamage: [...(s.floatingDamage || []), {
      id, x: x + jitterPx, y, value: amount, color: color || '#ef4444',
    }],
  }));
}

export function removeFloatingDamage(host, id) {
  host.setState((s) => ({
    floatingDamage: (s.floatingDamage || []).filter((d) => d.id !== id),
  }));
}

// Apply a subsurface change from the action menu. Codex Burrowing/Submerge:
// burrowing, submerging, surfacing and unburrowing are ordinary STEPS along
// the vertical edge of the unit's square. "(and that would use up their
// step)." Not a separate action. A unit's menu Burrow/Submerge/Surface is
// therefore a one-step Move and Attack: it rides the SAME server-validated
// move intent as a drag-move (same cell, different region), so the server
// applies the keyword edge gate, the tapped gate, the step budget, and the
// auto-tap in one atomic diff. The local flag flip + tap below are the same
// optimistic visuals the drag path applies, idempotent with the diff.
//
// An ARTIFACT has no Move and Attack ability. Its menu rows exist as the
// manual path for unautomated card effects (effect-driven bury/drown), which
// is forced movement: flag flip only, no step, no tap.
export function voluntarySubsurfaceMove(host, card, region) {
  if (isArtifact(card)) {
    host._setSubsurface(card, region);
    return;
  }
  host._setSubsurface(card, region);
  const mesh = host.meshes.get(card.id);
  const cell = card.cell
    || (mesh ? host._cellAtWorld(mesh.position.x, mesh.position.z) : null);
  const autoTap = host._automationsEnabled();
  if (cell) host._emitMoveIntent?.(card, cell, region || 'surface', autoTap);
  // Pay the move tap only if the unit survived the transition (a keyword-less
  // unit force-buried would already be in the cemetery, off the board) and the
  // session runs automated. The same gate the drag-move uses.
  // broadcast only when NO intent went out (no resolvable cell): with the
  // intent, its autoTap is the single server-side tap writer, and a stray
  // SET_TAPPED would tap the mirror even if the server rejects the move.
  if (mesh && !card.tapped && autoTap) {
    host._applyTap(card, mesh, true, { source: 'move', log: false, broadcast: !cell });
  }
}

export function setSubsurface(host, cardInstance, region) {
  // Oversized units (aura-minions covering >1 cell) can only be forced
  // fully under a region. One straddling a water + land border can be
  // neither submerged nor buried. The effect fizzles and the unit
  // stays on the surface instead of drowning. Single-cell units skip
  // this and keep the keyword-based survival rule below.
  if (region === 'underground' || region === 'underwater') {
    const mesh = host.meshes.get(cardInstance.id);
    const singleCell = mesh ? host._cellAtWorld(mesh.position.x, mesh.position.z) : null;
    const cells = unitFootprintCells(cardInstance, singleCell);
    if (cells.length > 1) {
      const fits = region === 'underwater'
        ? canForceSubmergeAll(cardInstance, cells, host.meshes, host._cellAtWorld, host._getFullCard)
        : canForceBurrowAll(cardInstance, cells, host.meshes, host._cellAtWorld, host._getFullCard);
      if (!fits) {
        const card = cardLogLabel(cardInstance);
        if (region === 'underwater') host.logAction('{card} is too large to submerge fully. It stays on the surface', { card });
        else host.logAction('{card} is too large to burrow fully. It stays on the surface', { card });
        return;
      }
    }
  }
  const wasSubsurface = !!cardInstance.subsurface;
  cardInstance.subsurface = region;
  host._broadcastCardSubsurface?.(cardInstance.id, region);
  const label = cardLogLabel(cardInstance);
  if (region == null) host.logAction('{card} surfaced', { card: label });
  else if (region === 'underground') host.logAction('{card} burrowed', { card: label });
  else host.logAction('{card} submerged', { card: label });

  // Force-bury / force-drown death. Only when the region is
  // subsurface AND the card lacks the survival keyword. Surface
  // moves never kill. Codex Underground: "Whenever a minion
  // without Burrowing occupies an underground location, it
  // immediately dies." Symmetric Underwater for Submerge.
  // Manual session: the codex death rule is the players' to enforce. The
  // card just changes region and stays wherever they put it.
  const lethal = host._automationsEnabled?.() !== false && (
    (region === 'underground' && !survivesForcedBury(cardInstance))
    || (region === 'underwater' && !survivesForcedSubmerge(cardInstance)));
  if (lethal) {
    if (region === 'underground') host.logAction('{card} died in the underground', { card: cardLogLabel(cardInstance) });
    else host.logAction('{card} died in the underwater', { card: cardLogLabel(cardInstance) });
    host.sendCardToPile(cardInstance, 'Cemetery');
    // sendCardToPile triggers Deathrite + leave-play reset; no
    // need for a separate recompute here.
    return;
  }

  // on_surface. A subsurface card just came up. Fires from this single
  // subsurface choke point (region === null transition). Buried Treasure
  // / Sunken Treasure: "When carried to the surface, its controller
  // sacrifices it and draws two cards." The sacrifice inside the trigger
  // removes the card, so fire it before the recompute below settles the
  // (now-departed) card into the aura board.
  if (region == null && wasSubsurface) {
    host.applyCardAutomation(cardInstance, [TRIGGER.ON_SURFACE]);
    // Equipped cards have no own mesh and share the carrier's region.
    // Equipping clears their `subsurface` (resetCardOnLeavePlay runs on the
    // leave-play hook), so it cannot gate this walk. When a subsurface
    // carrier emerges, every card it carries surfaces with it. Fire each
    // one's on_surface clause (Buried/Sunken Treasure: "When carried to the
    // surface, its controller sacrifices it and draws two cards"). A card
    // with no on_surface entry no-ops.
    for (const eq of [...(cardInstance.equippedCards || [])]) {
      eq.subsurface = null;
      host._broadcastCardSubsurface?.(eq.id, null);
      host.applyCardAutomation(eq, [TRIGGER.ON_SURFACE]);
    }
    // An on_surface clause may sacrifice the carried card (Buried/Sunken
    // Treasure: draw two, then sacrifice itself). A sacrificed card now
    // lives in a pile, so drop it from the carrier's equipment rather than
    // leave a stale carry reference behind.
    if (cardInstance.equippedCards?.length) {
      const piled = new Set(
        (host.state?.gameState?.piles || []).flatMap((p) => (p.cards || []).map((c) => c.id)),
      );
      const kept = cardInstance.equippedCards.filter((eq) => !piled.has(eq.id));
      if (kept.length !== cardInstance.equippedCards.length) {
        cardInstance.equippedCards = kept;
        host._broadcastCardEquipment?.(cardInstance.id, kept);
      }
    }
  }

  host.recomputeStaticAuras();
  host.forceUpdate();
}

// W3. Flag a cast as OUTSIDE the server's automated debit (manual mana
// management, or a locally-consumed Apex free-cast window). The cast emit
// (_emitCastIntent / castMagicFromHand) consumes the flag into the intent /
// command's `autoPay: false`, so the server skips its computed debit.
export function markCastAutoPayWaived(host, cardId) {
  if (!host._castAutoPayWaived) host._castAutoPayWaived = new Set();
  host._castAutoPayWaived.add(cardId);
}

export function consumeCastAutoPayWaiver(host, cardId) {
  if (!host._castAutoPayWaived?.has(cardId)) return false;
  host._castAutoPayWaived.delete(cardId);
  return true;
}

// Deduct the mana cost from the controller's tracker. Thresholds
// are gates, not consumed. Nothing to deduct for those. Sites
// bypass (isFreeToPlay short-circuits getCardCost-callers; this
// helper double-checks via guardian.type for safety).
//
// Auth path (W3): the debit is SERVER-computed at the cast commit (castService
// computeCastDebit) and folds into the HUD from the diff. No local payMana,
// no resource:payMana intent. This function only decides the manual-mana
// autoPay opt-out and stamps the waiver the cast emit consumes; the Apex
// free-cast window is server state the debit prices 0 and consumes itself.
export function payCastingCost(host, card, fullCard, destinationCell = null) {
  const auth = !!host._authZonesEnabled?.();
  // Manual session: the player manages mana themselves, so we skip the
  // entire deduction (including cost-modifier consumption. A Templar
  // discount only means something if mana is actually being charged).
  if (!host._automationsEnabled()) {
    if (auth) markCastAutoPayWaived(host, card.id);
    return;
  }
  if (!fullCard) return;
  const type = fullCard.type ?? fullCard.guardian?.type;
  if (type === 'Site') return;
  const cost = getCardCost(fullCard);
  const controller = card.rotated ? 'p2' : 'p1';
  // The Apex of Babel free-cast window: the next spell the controller
  // plays costs no mana. Auth: the window is SERVER state. ComputeCastDebit
  // prices the cast 0 and consumes the grant at the commit, so nothing is
  // waived or consumed locally. Sandbox: consume the local one-shot grant
  // and charge nothing.
  if (host._isFreeCastActive?.(controller)) {
    if (auth) return;
    host._consumeFreeCast(controller);
    host.forceUpdate?.();
    return;
  }
  if (auth) return; // server-computed debit rides the cast commit's diff
  // v1.89 / v1.99 / v1.111. Pay the modifier-adjusted cost. v1.111:
  // pass the destination cell so cell-restricted modifiers (Harbinger)
  // only apply when the cast lands on a marked square. Consume one
  // use on matching per-turn-capped modifiers AFTER charging (so
  // failed casts don't burn a Templar use).
  const { need: effectiveCost, discounted } = host.effectLedger.castManaBreakdown(
    controller, cost.mana, fullCard, destinationCell,
  );
  if (discounted) {
    host.effectLedger.consumeCostModifierUse(controller, fullCard, destinationCell);
  }
  if (effectiveCost <= 0) return;
  host.playerResources.payMana(controller, effectiveCost);
}

export function flipCard(host, cardInstance, mesh, opts = {}) {
  if (!opts.fromAbility && !host._canFlipByHotkey(cardInstance)) {
    host._toastFlipLocked(cardInstance);
    host.setState({ contextMenu: null });
    return;
  }
  cardInstance.faceDown = !cardInstance.faceDown;
  // Flipping a card face up reveals it to the opponent. Its name is now
  // public for the logbook and stays so (sticky) even if flipped back down.
  if (!cardInstance.faceDown) cardInstance.nameKnown = true;
  syncCardFace(cardInstance);
  animateCardFlip(mesh, cardInstance);
  playSound('cardFlip');
  host._broadcastFlip(cardInstance, cardInstance.faceDown);
  const isTwoSided = Boolean(cardInstance.backFaceImagePath || cardInstance.backFaceImageUrl);
  // cardLogLabel keys off the just-toggled faceDown: flipping a card UP
  // reveals it, so the log names it from here on; flipping it DOWN hides
  // it, so the name is withheld. (Two-sided cards are always named. Both
  // faces are public.)
  const flipped = cardLogLabel(cardInstance);
  if (isTwoSided && cardInstance.faceDown) host.logAction('Flipped {card} to back face', { card: flipped });
  else if (isTwoSided) host.logAction('Flipped {card} to front face', { card: flipped });
  else if (cardInstance.faceDown) host.logAction('Flipped {card} face down', { card: flipped });
  else host.logAction('Flipped {card} face up', { card: flipped });
  host.setState({ contextMenu: null });
}

// Single controller-flip orchestrator. Both the right-click "switch
// ownership" menu action and the inbound card:ownership sync funnel
// through here so the controller change, its visual fallout, the
// static-aura re-segregation, and the summoning-sickness reconciliation
// all happen in exactly one place. Mirrors the _applyTap primitive
// pattern: takes an ABSOLUTE target `rotated` and no-ops if unchanged.
//
// `broadcast`/`log` are true for the local actor and false for the
// remote receiver (the peer enforced its own policy and writes the
// canonical log line on its actor side).
//
// Returns true iff the controller actually changed.
export function applyControllerFlip(host, card, mesh, nextRotated, { broadcast = true, log = true } = {}) {
  if (!card || !mesh) return false;
  if (card.rotated === nextRotated) return false;
  card.rotated = nextRotated;
  // Reuse the tap tween. ComputeTargetZ folds `rotated` into the
  // target Z so the same path animates the 180° controller flip.
  animateCardTap(mesh, card);
  playSound('cardPlace');
  // Ownership ring colour + ATK/HP pill orientation follow the new
  // controller.
  host.applyOwnershipOutline(mesh);
  updateLifeHUDOrientation(host.lifeHUDs.get(card.id), card, host.state.isHost);
  updateCounterHUDOrientation(host.counterHUDs.get(card.id), card, host.state.isHost);
  // Sites re-snap to the new controller's near edge. Both peers derive
  // the snap independently from the rotated flag, so no extra position
  // broadcast is needed.
  if (card.isSite) host._snapSiteToOwnerEdge(card, mesh, { broadcast: false });
  // Reconcile control-dependent transient state (stale summoning
  // sickness the previous controller's untap hasn't cleared). Pure
  // rule lives in engine/core/controlChange.js.
  const { clearedSickness } = reconcileControlChange(
    card, turnKey(host.state.currentTurn, host.state.turnNumbers),
  );
  if (clearedSickness) host.rebuildStatusBadges(card.id);
  // Controller flip changes which side "controller: self/allied/enemy"
  // scopes resolve to. Re-run so every ownership-relative aura (buffs
  // like Jihad, control claims) re-segregates onto the correct side. The
  // recompute also re-derives any owner-locked unit hand's controller from
  // this card's new control (reconcileUnitHandControllers), so the hand
  // follows automatically. No separate flip call needed.
  host.recomputeStaticAuras();
  if (broadcast) host._broadcastOwnership(card.id, card.rotated);
  if (log) {
    const nowOwned = host.isOwnedByLocalPlayer({ type: 'card', cardInstance: card });
    if (nowOwned) host.logAction('Took control of {card}', { card: cardLogLabel(card, msg('a card')) });
    else host.logAction('Gave control of {card}', { card: cardLogLabel(card, msg('a card')) });
  }
  return true;
}

export function switchCardOwnership(host, cardInstance, mesh) {
  if (!cardInstance || !mesh) { host.setState({ contextMenu: null }); return; }
  host._applyControllerFlip(cardInstance, mesh, !cardInstance.rotated, { broadcast: true, log: true });
  host.setState({ contextMenu: null });
}

export function tapCard(host, cardInstance, mesh) {
  // Action-menu / double-click Tap-Untap. Toggle through the
  // unified orchestrator; surface the disabled toast here (call-
  // site UX policy, not engine rule) when the rule refuses.
  const next = nextTapToggle(cardInstance);
  if (next === null) {
    toast.message(t("{name} is disabled. Can't tap.", { name: cardInstance.name || t('Card') }));
    return;
  }
  host._applyTap(cardInstance, mesh, next, { source: 'action_menu' });
  host.setState({ contextMenu: null });
}

// ── Status effects ──────────────────────────────────────────────
export function toggleCardStatus(host, cardInstance, statusKey) {
  // Toggle against the EFFECTIVE state so printed keywords like
  // Airborne actually flip OFF when the player clicks them in the
  // ring. ApplyStatusToggle handles the deny-list mechanics so
  // suppression survives until the card leaves the field.
  const wasActive = getEffectiveStatuses(cardInstance).includes(statusKey);
  const nextActive = !wasActive;
  const changed = applyStatusToggle(cardInstance, statusKey, nextActive);
  if (!changed) return;
  // Charge granted late (radial menu, or an effect routing through here)
  // must clear the sickness flag set at summon, or the unit still can't
  // pay move/attack taps despite the badge. The card:status receiver
  // mirrors this for the peer.
  if (statusKey === 'charge' && nextActive) applyLateCharge(cardInstance);
  // Codex Stealth / Ward: becoming disabled or silenced strips the
  // Stealth and Ward tokens (permanently). Consume BEFORE the badge
  // rebuild so the badge + 3D effect drop, and tell the peer so its
  // copy loses them too.
  if (loseStealthIfDisabledOrSilenced(cardInstance)) {
    host._broadcastCardStatus(cardInstance.id, 'stealth', false);
  }
  if (loseWardIfDisabledOrSilenced(cardInstance)) {
    host._broadcastCardStatus(cardInstance.id, 'ward', false);
  }
  host.rebuildStatusBadges(cardInstance.id);
  // Silence / disable suppress "Movement +N." Repaint the chip so the
  // board reflects the suppressed (or restored) bonus.
  if (statusKey === 'silenced' || statusKey === 'disabled') host._refreshMovementHUD(cardInstance.id);
  host._broadcastCardStatus(cardInstance.id, statusKey, nextActive);
  // v1.17b: re-resolve every static aura. Disabled toggling flips
  // a card's contribution as an aura source; ward toggling changes
  // Faith Incarnate's per-ward power count; any keyword status
  // change can ripple into per-each scaling. Cheap to always rerun.
  host.recomputeStaticAuras();
}

export function clearAllStatuses(host, cardInstance) {
  if (!cardInstance.statuses || cardInstance.statuses.length === 0) return;
  const removed = [...cardInstance.statuses];
  cardInstance.statuses = [];
  host.rebuildStatusBadges(cardInstance.id);
  for (const key of removed) {
    host._broadcastCardStatus(cardInstance.id, key, false);
  }
}

export function rebuildStatusBadges(host, cardId) {
  const mesh = host.meshes.get(cardId);
  if (!mesh) return;
  const card = mesh.userData.cardInstance;

  // Remove existing badge meshes from the card.
  const existing = host.statusHUDs.get(cardId) || [];
  for (const badge of existing) {
    mesh.remove(badge);
    badge.geometry.dispose();
    badge.material.map?.dispose();
    badge.material.dispose();
  }

  // Render the union of manually-set and aura-granted statuses. The
  // Unified effective status (v1.16a): manual + aura + printed,
  // with the 'disabled' override collapsing everything to a single
  // DIS badge when active. Inline helper would have been:
  //   [...new Set([...(card.statuses || []), ...(card._auraStatuses || [])])]
  // but disabled handling and printed-keyword unification are
  // valuable everywhere. One helper, three call sites.
  // Badges show the DISPLAY status union. Effective statuses plus
  // equipment-derived pseudo-statuses (Lance). 3D effects key off the
  // real effective statuses only (Lance has no atmospheric visual; it
  // shows as a badge + the equipped-token overlay).
  const badgeKeys = displayStatusKeys(card);
  const statuses = fxStatusKeys(card);
  if (badgeKeys.length === 0) {
    host.statusHUDs.delete(cardId);
  } else {
    const badges = buildStatusBadges(badgeKeys, card.isSite);
    for (const badge of badges) mesh.add(badge);
    host.statusHUDs.set(cardId, badges);
  }

  // Status keywords that have a registered visual in
  // CARD_STATUS_EFFECTS (stealth → smoke, ward → shield, …) get a
  // 3D effect spawned/disposed alongside the 2D badge. Statuses
  // not in the registry get the badge only.
  host._syncCardEffects(cardId, statuses);

  // Keep the DOM hover overlay in sync if the rebuilt card is the
  // one currently under the cursor.
  if (host.hoveredMesh?.userData?.cardInstance?.id === cardId) {
    host.syncHoverStatusOverlay();
  }
}

// Keys the 3D effect layer reads: the real effective statuses plus the
// derived summoning-sickness key. Lance stays out (no visual of its own);
// sickness is appended so its sleep glyphs run while the stamp is live.
function fxStatusKeys(card) {
  const statuses = getEffectiveStatuses(card);
  return hasSummoningSickness(card) ? [...statuses, 'summoning_sick'] : statuses;
}

// Reconcile the active visual effects on a card against its current
// status set. Add any newly-active status that has a registered
// effect; dispose any effect whose status was just dropped.
export function syncCardEffects(host, cardId, statuses) {
  // v1.109. Pref-gated. When OFF the player gets a "clean board":
  // status badges still show (small 2D pills), but the in-scene
  // particle/mesh effects (Stealth smoke, Ward shield, Airborne
  // float, Ranged glow, etc.) are suppressed. Treated as an empty
  // active-set so the tear-down branch disposes anything currently
  // alive and the spawn branch never instantiates new ones.
  //
  // At most ONE 3D VFX runs per card. Stacking 4+ full-card effects
  // turned the card into an unreadable particle soup. The badge
  // stack still shows every status (informational layer), but the
  // atmospheric layer picks the first status per STATUS_EFFECTS
  // order (the same order badges stack in) that has a registered
  // visual.
  const active = new Set();
  if (statuses?.length) {
    const present = new Set(statuses);
    for (const { key } of STATUS_EFFECTS) {
      if (present.has(key) && CARD_STATUS_EFFECTS[key]) {
        active.add(key);
        break;
      }
    }
  }
  let effects = host.cardEffects.get(cardId);

  // Tear down effects whose status was removed.
  if (effects) {
    for (const [statusKey, effect] of effects) {
      if (!active.has(statusKey)) {
        CARD_STATUS_EFFECTS[statusKey]?.dispose(effect);
        effects.delete(statusKey);
      }
    }
  }

  // Spawn effects for newly-active statuses that have a registered
  // visual but aren't currently materialised.
  const mesh = host.meshes.get(cardId);
  if (mesh) {
    // Effect groups parent to the scene root (not the card mesh) for
    // independent orientation, so they don't inherit a token card's
    // 0.5× mesh scale. Apply it explicitly here. Update*() only
    // writes position/rotation, so the scale survives the lifetime.
    const isToken = !!mesh.userData?.cardInstance?.isToken;
    for (const statusKey of active) {
      const def = CARD_STATUS_EFFECTS[statusKey];
      if (!def) continue;
      if (effects?.has(statusKey)) continue;
      const effect = def.create();
      if (isToken) effect.points.scale.setScalar(TOKEN_CARD_SCALE);
      host.scene.scene.add(effect.points);
      if (!effects) {
        effects = new Map();
        host.cardEffects.set(cardId, effects);
      }
      effects.set(statusKey, effect);
    }
  }

  if (effects && effects.size === 0) {
    host.cardEffects.delete(cardId);
  }
}

// v1.109. Re-run the per-card effect sync for every card on the
// board. Called when the player flips the "Show status effects"
// pref so the change takes effect immediately (without it the
// toggle would only apply to cards entering or changing status
// AFTER the flip).
export function resyncAllCardEffects(host) {
  for (const [cardId, mesh] of host.meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card) continue;
    host._syncCardEffects(cardId, fxStatusKeys(card));
  }
}

// Drop every active visual effect for a card. Called from each
// removeCardFromTable / banish / sync-remove path so the effects
// never outlive their card mesh.
export function removeAllCardEffects(host, cardId) {
  const effects = host.cardEffects.get(cardId);
  if (!effects) return;
  for (const [statusKey, effect] of effects) {
    CARD_STATUS_EFFECTS[statusKey]?.dispose(effect);
  }
  host.cardEffects.delete(cardId);
}

export function tickCardEffects(host, dt) {
  if (host.cardEffects.size === 0) return;
  for (const [cardId, effects] of host.cardEffects) {
    const mesh = host.meshes.get(cardId);
    if (!mesh) {
      // Defensive: the card mesh went away without us noticing.
      // Tear the effects down so they don't leak.
      for (const [statusKey, effect] of effects) {
        CARD_STATUS_EFFECTS[statusKey]?.dispose(effect);
      }
      host.cardEffects.delete(cardId);
      continue;
    }
    // The card's renderOrder is reassigned EVERY FRAME by
    // _applyComputedY (line ~1840) based on stack rank. When the
    // player lifts/drags a card it becomes the topmost rank and
    // gets the highest renderOrder of the lot. Often higher than
    // the static renderOrder values baked into the effect meshes
    // at create-time. With the opaque card rendered AFTER the
    // effect, the card overdraws the effect (the lift bug:
    // animation "disappears" when you pick the card up, gradually
    // returns as restacking shifts the lifted card's rank back
    // down). Fix: rebase every effect mesh's renderOrder relative
    // to its card's current renderOrder, preserving the relative
    // order between effect layers via the cached `_baseRenderOrder`.
    const cardRO = mesh.renderOrder || 0;
    for (const [statusKey, effect] of effects) {
      CARD_STATUS_EFFECTS[statusKey]?.update(effect, dt, mesh);
      effect.points.traverse((child) => {
        if (!child.isMesh) return;
        if (child.userData._baseRenderOrder === undefined) {
          child.userData._baseRenderOrder = child.renderOrder || 0;
        }
        child.renderOrder = cardRO + 100 + child.userData._baseRenderOrder;
      });
    }
  }
}

// v1.115. Per-card finalisation for a freshly played card. Extracted
// from the drag-commit loop so the Avatar of Fire ignite intercept can
// defer + re-run it from the "Play as Site" prompt resolver. Every
// call must satisfy the same invariants as the original inline block:
// mana paid, harbinger flash, site grant + auto-tap consumed, play log
// line, aura snap, site-play effects + realm entry watchers, geomancer
// / druid bonuses, summoning sickness stamp.
// Cast a Magic spell from hand. The cast ceremony is unchanged (storyline
// dwell → spellcaster pick → prompts), but the resolved spell now SETTLES ON
// THE BOARD at the cell it was cast to instead of going to the cemetery. The
// codex "magic spells go to the cemetery after they resolve" routing is
// reverted for now. This is the cast counterpart to the realm-entry commit
// that minions / sites / auras / artifacts take. Animist's cast-as-Spirit
// magics are transformed to Minions in addCardToTable, so they never get here.
export function castMagicFromHand(host, card) {
  // Owner-locked spell being cast leaves its unit hand (idempotent. The
  // realm-entry commit may already have pulled it). The cast committed, so
  // clear the in-flight unit-hand drag marker.
  // Capture the owner-lock BEFORE removeCardFromTable below. ResetCardOnLeavePlay
  // wipes _castableOnlyBy the instant the staged mesh leaves the table, and
  // routeSpellPlay (in the deferred finish() automation) reads it to skip the
  // caster pick. A spell held in a special spellcaster's hand has exactly one
  // legal caster (its owner), so prompting "who casts this?" is illegal; the
  // re-stamp below keeps the lock alive through the cast.
  const lockOwner = card._castableOnlyBy || null;
  if (lockOwner) host.removeFromUnitHand(lockOwner, card.id);
  if (host._unitHandDrag?.spellId === card.id) host._unitHandDrag = null;
  const controller = card.rotated ? 'p2' : 'p1';
  // A magic is PLAYED TO THE STORYLINE first, then lands on the board. So:
  // (1) the spell dwells off-square. Stash the drop point from the staged
  // drag mesh, then drop that mesh (local-only; the staged place was never
  // broadcast for magics); (2) show it on the storyline while it resolves;
  // (3) after a brief dwell, resolve it and put it on the board via the SAME
  // path every card uses to enter the realm. ApplyCardAutomation's Magic
  // branch → landCastSpellOnBoard → addCardToTable.
  const stagedMesh = host.meshes.get(card.id);
  if (stagedMesh) {
    card.x = stagedMesh.position.x;
    card.z = stagedMesh.position.z;
    host.removeCardFromTable(card, false, { staged: true });
  }
  // Re-stamp the owner-lock the table-leave reset just wiped, so the deferred
  // routeSpellPlay forces the owner as the sole caster (no illegal pick).
  if (lockOwner) card._castableOnlyBy = lockOwner;
  host.logAction('Cast {card}', { card: cardLogLabel(card, msg('a spell')) });
  const fullCard = host._getFullCard(card.cardId);
  // Online, the opponent's storyline acknowledge renders from the PUBLIC
  // storyline zone fold (syncStorylineFromMirror). No relay needed.
  const entry = {
    id: `cast-${card.id}`,
    cardName: fullCard?.name || card.name || '',
    imageUrl: card.imageUrl || null,
    rotated: !!card.rotated,
  };
  host.setState((s) => ({ castingSpells: [...(s.castingSpells || []), entry] }));
  // Server-authoritative storyline: the spell now occupies the public
  // `storyline` zone so a disconnect mid-cast leaves it durably dwelling
  // (the pending prompt is re-derived from the restored card on reload)
  // instead of vanishing. The landing path (landCastSpellOnBoard) emits the
  // follow-on storyline → play move. No-op in non-auth matches.
  // W3: the server debits the Magic's mana off this command's commit;
  // autoPay:false carries the manual-mana / free-cast opt-out.
  host._emitMoveToZone?.(card.id, 'storyline',
    consumeCastAutoPayWaiver(host, card.id) ? { autoPay: false } : undefined);
  // Commit the cast cell up front, while the drop point is still known: the
  // spell has to land there once it resolves, and a disconnect mid-cast would
  // otherwise restore a storyline card with no square to come back to.
  host._emitPosition?.(card.id, card.x, card.z);
  // Dwell is the on-storyline beat before the card lands on its square.
  // The test bridge sets it to 0 so resolution is synchronous.
  const dwell = host._castDwellMs ?? 900;
  return new Promise((resolve) => {
    const finish = () => {
      // Track the casting card so every later lookup by id resolves it even
      // before its mesh exists (maybeDisposeFinishedSpell, the prompt paths).
      if (!host._castingSpellCards) host._castingSpellCards = {};
      host._castingSpellCards[card.id] = card;
      // The dwell is over, so the spell enters the realm NOW. BEFORE its
      // on_play resolves, exactly like every other card (addCardToTable →
      // automation). The landing must not wait on how the effect resolves:
      // an on_play owned by the SERVER (a pendingChoice modal) never touches
      // `_spellPlayInFlight`, so gating the landing on that flag left the
      // spell parked in the storyline forever (Mad Dash: draw-source modal +
      // target pick are both server prompts). Prompts now resolve against a
      // card that is already on its square.
      host._landCastSpellOnBoard(card);
      host.applyCardAutomation(card, ['on_play'], { controller });
      // Target magics are still resolving; keep the ref until
      // maybeDisposeFinishedSpell clears them.
      if (!host._spellPlayInFlight?.has(card.id)) delete host._castingSpellCards[card.id];
      host.setState((s) => ({ castingSpells: (s.castingSpells || []).filter((f) => f.id !== entry.id) }));
      resolve(card.id);
    };
    if (dwell <= 0) finish();
    else setTimeout(finish, dwell);
  });
}

export function finalizeFreshPlayCommit(host, card, m) {
  // Owner-locked spell committing to a realm play (minion / aura /
  // artifact) leaves its unit hand. Magics route on to castMagicFromHand
  // below, which removes it there (idempotent).
  if (card._castableOnlyBy) host.removeFromUnitHand(card._castableOnlyBy, card.id);
  if (host._unitHandDrag?.spellId === card.id) host._unitHandDrag = null;
  const fullCard = host._getFullCard(card.cardId);
  const dropCell = host._cellAtWorld(m.mesh.position.x, m.mesh.position.z);
  host._payCastingCost(card, fullCard, dropCell);
  host._maybeFireHarbingerSummonFlash(card, fullCard, dropCell);

  // Animist "summon a magic as a Spirit" resolves at the authoritative
  // play-commit, not at provisional drag-staging (a card bounced back to
  // hand mid-drag would have its staged transform reverted by the
  // leave-play reset). When the controller has armed the ability, flip the
  // committed magic into a Spirit Minion (power = cost) here, BEFORE the
  // magic→storyline routing below, so it enters the realm instead of
  // casting. The transform is idempotent and attaches the stat HUD the
  // Magic card never got (mirrors the Enchantress animate op).
  const playController = card.rotated ? 'p2' : 'p1';
  if (card.type === 'Magic' && host._animistSpiritArm?.[playController]) {
    const avatarRules = resolveAvatarRules(host._findAvatarCard(!!card.rotated));
    if (shouldCastAsSpirit(card, avatarRules) && applyCastAsSpiritTransform(card, fullCard)) {
      host._attachLifeHUDIfMissing(card);
      host._refreshLifeHUD(card);
      host._disarmAnimistSpirit(playController);
    }
  }

  // A magic dwells on the storyline before it settles on its square, so it
  // takes the cast path instead of this realm-entry commit. The landing
  // (landCastSpellOnBoard) re-adds it at the drop cell once it has resolved.
  // card.type (not fullCard.type) is checked so Animist's cast-as-Spirit
  // magics. Already transformed to Minions. Fall through.
  // Manual session: there is no automation to resolve and nothing to
  // acknowledge, so the storyline detour (and its ack prompt) is skipped.
  // the magic lands on the table like any other card, and the players move
  // it to the cemetery themselves once they've settled its effect.
  if (card.type === 'Magic' && host._automationsEnabled?.() !== false) {
    host._castMagicFromHand(card);
    return;
  }

  // Auth: the server consumes the site grant at the cast commit (castService)
  // The gate reads the folded allowance, so no local decrement.
  if (m._siteGrantToConsume && !host._authZonesEnabled?.()) {
    const ctl = card.rotated ? 'p2' : 'p1';
    host._siteGrants[ctl] = Math.max(0, (host._siteGrants[ctl] || 0) - 1);
    if (host._siteGrants[ctl] === 0 && host._siteGrantRestriction) {
      host._siteGrantRestriction[ctl] = null;
    }
  }
  if (m._siteAutoTapAvatarId && host._automationsEnabled()) {
    const avMesh = host.meshes.get(m._siteAutoTapAvatarId);
    if (avMesh) {
      host._applyTap(
        avMesh.userData.cardInstance,
        avMesh,
        true,
        { source: 'site_play_shortcut' },
      );
    }
  }

  const label = card.faceDown ? msg('a card face down') : (card.name || msg('a card'));
  const cellNumber = host._cellNumberAt(m.mesh.position.x, m.mesh.position.z);
  if (cellNumber != null) host.logAction('Played {card} on [{cell}]', { card: label, cell: cellNumber });
  else host.logAction('Played {card}', { card: label });
  // Auth zones: the card has entered the realm (board). Register zone='play' on
  // the server so the opponent's authoritative mirror sees it as a PUBLIC card.
  // without this it stays hidden (its origin pile) server-side and card-state
  // commands for it (tap / stats / status) silently no-op for the opponent. The
  // click-play path (playCardFromHand) already emits this; the DRAG commit (this
  // function) is the path that was missing it. Magics returned above (never the
  // board); the local actor is the only client running this commit.
  // P5-cast-move: route the realm-entry through a server-VALIDATED cast intent
  // (owner-lock + positional anti-cheat) that commits zone + position atomically.
  // `_emitCastIntent` flags the card as intent-owned so the legacy MOVE_TO_ZONE
  // ('play') + SET_POSITION commands for it are suppressed (single writer). Mana
  // stays owned by the P4 resource intent (payCastingCost already routed it).
  // The cast intent is best-effort: off the auth path it no-ops and the legacy
  // _emitMoveToZone below runs unchanged. The local actor optimistically applied
  // the play already; the server diff reconciles and the opponent renders it.
  // Snap sites/auras to their cell anchor BEFORE the cast intent so the server
  // records the snapped resting coords, not the raw drop point. A reload
  // restores the stored pos verbatim, so an unsnapped commit reloads unsnapped.
  if (card.isSite) host._snapSiteToOwnerEdge(card, m.mesh);
  if (card?.type === 'Aura') host._snapAuraToAnchor(card, m.mesh);
  if (host._authZonesEnabled?.()) host._emitCastIntent?.(card, m);
  if (host._authZonesEnabled?.()) host._emitMoveToZone(card.id, 'play');
  if (!card.faceDown) {
    host.applySitePlayEffects(card);
    host._fireRealmTrigger(TRIGGER.ON_REALM_ENTRY, card);
    if (card.isSite) {
      // Auth: the server conjures the Geomancer Rubble itself
      // (castService → conjureGeomancerRubble). Firing it here too
      // client-minted a second, phantom Rubble the mirror had never
      // heard of (GH-71's poisoned cell set).
      if (!host._authZonesEnabled?.()) host._maybeFireGeomancerEarthBonus(card);
      host._maybeFireDruidTawnySummon(card);
    }
    // on_spell_cast watchers (Enchantress: "Whenever you cast a
    // spell, you may animate target aura"). Codex "Spell" = any
    // card in the spellbook. Minion, Artifact, Aura, Magic. Sites
    // are atlas cards, NOT spells, so they only fire the GLOBAL-scope
    // "Whenever anyone plays a card" watchers (The Immortal Throne);
    // fireOnSpellCast filters site plays to global scope internally.
    // Magic spells are routed through applyCardAutomation's Magic
    // branch which fires _fireOnSpellCast directly; gating on
    // `type !== 'Magic'` here avoids a double-fire.
    if (fullCard?.type !== 'Magic') {
      host._fireOnSpellCast(card);
    }
    // on_enter_location watchers (Mariner's Curse, Rest in Peace,
    // Druid-back-face's site-granted damage) need to fire for
    // freshly-summoned units too. Not just intra-grid moves. The
    // intra-grid loop later in handleMouseUp skips fresh plays via
    // `freshPlayIds.has(...)`, so the trigger was previously missed
    // for summon-onto-covered-cell scenarios. Sites + auras are
    // the location, not entrants; only units (Minion / Avatar)
    // count as "entering" a cell.
    if (!card.isSite && card.type !== 'Aura' && card.type !== 'Artifact') {
      const enterCell = host._cellAtWorld(m.mesh.position.x, m.mesh.position.z);
      if (enterCell) {
        host._fireOnEnterLocationAt(card, enterCell, 'summon');
        // on_summon_here watchers anchored at the summon cell (The Round
        // Table: "Whenever you summon ... to The Round Table, draw a
        // card"). Summons skip on_stop / on_enter_location-on-mover, so
        // this is the only path that lets a card react to a fresh summon
        // landing on its own cell.
        host._fireOnSummonHere(card, enterCell);
      }
    }
  }
  // qa#121: summoning sickness is a rule the game keeps for the players, so a
  // manual session never stamps it (the server stopped in qa#121's first pass;
  // this is the client half). The stamp also drives `_chargeFresh`, which the
  // turn boundary reads to suppress a Charge badge the players never placed.
  if (host._automationsEnabled?.() !== false) {
    markSummonedThisTurn(card, turnKey(host.state.currentTurn, host.state.turnNumbers));
    // The sickness badge + sleep glyphs are derived from the stamp.
    host.rebuildStatusBadges?.(card.id);
  }
  // Force a server snapshot push NOW (not debounced): a card played then a quick
  // disconnect would otherwise vanish for the player on reconnect. The auth
  // MOVE_TO_ZONE('play') is durable, but the legacy board blob (which still owns
  // card POSITIONS, Domain 5) is what the reconnect rebuilds the table from, and
  // a debounced save may not have captured the play before the socket dropped.
  // The opponent (never disconnected) keeps the card, so without this the two
  // boards diverge. Force-write closes that window.
  host.autoSave?.({ force: true })?.catch?.(() => {});
  // Only the local actor runs this commit, so "the local player just
  // played a unit" is implicit here. The card id lets the tutorial
  // spotlight the played card's mesh.
  if (countsAsUnit(card)) host._queueEventTutorial?.(FIRST_UNIT_TUTORIAL_KEY, { cardId: card.id });
}

// v1.103. Apply a stat buff that reverts on a specific player's
// turn-end at or after a target turn number. Used by Witch's -3
// power curse to honor "until your next turn" timing across the
// opponent's intervening turn. Mutation + revert mirror
// _applyThisTurnStatBuff but the consume happens via
// _delayedStatReverts processed in _stepPruneExpiredEffects.
export function applyDelayedStatBuff(host, card, delta, { revertPlayer, revertAtTurnNumber }) {
  if (!card) return;
  card.currentAttack = (card.currentAttack ?? card.baseAttack ?? 0) + delta;
  host._broadcastCardStat(card.id, 'atk', card.currentAttack);
  if (!host._delayedStatReverts) host._delayedStatReverts = [];
  host._delayedStatReverts.push({ cardId: card.id, delta, revertPlayer, revertAtTurnNumber });
  const hud = host.lifeHUDs.get(card.id);
  if (hud?.sprite) {
    const display = card.currentAttack + (card._auraStatDelta || 0);
    updateLifeHUD(hud.sprite, display, 'atk', display - (card.baseAttack ?? display));
  }
}

// Grant a status keyword to a target and honor the firing entry's
// duration. The status applies now (synced through toggleCardStatus)
// and, for timed grants, a revert is queued that toggles it off at the
// right turn boundary. Mirroring _applyThisTurnStatBuff /
// applyDelayedStatBuff. `revert` is null for permanent / instant /
// until_leaves grants (no revert queued), `{ kind: 'this_turn' }` to
// expire at the controller's turn end, or `{ kind: 'until_next_turn',
// revertPlayer, revertAtTurnNumber }` to expire at the start of the
// controller's next turn.
export function applyGrantStatusToTarget(host, card, keyword, revert) {
  if (!card || !keyword) return;
  if (!getEffectiveStatuses(card).includes(keyword)) {
    host.toggleCardStatus(card, keyword);
  }
  if (!revert) return;
  if (revert.kind === 'this_turn') {
    if (!host._thisTurnStatusReverts) host._thisTurnStatusReverts = [];
    host._thisTurnStatusReverts.push({ cardId: card.id, keyword });
  } else if (revert.kind === 'until_next_turn') {
    if (!host._delayedStatusReverts) host._delayedStatusReverts = [];
    host._delayedStatusReverts.push({
      cardId: card.id,
      keyword,
      revertPlayer: revert.revertPlayer,
      revertAtTurnNumber: revert.revertAtTurnNumber,
    });
  }
}

// v1.77e. Transient self-buff applied from a replacement effect.
// Mutates currentAttack so combat math + HUD pick it up immediately,
// queues a revert that fires in _stepPruneExpiredEffects at end of
// turn. Askelon Phoenix's "+1 power this turn instead" pattern.
export function applyThisTurnStatBuff(host, card, delta, stat = 'atk') {
  if (!card) return;
  if (stat === 'mov') {
    // v1.102. Bladedancer pattern: bonus movement this turn that
    // reverts at end of turn. Stores delta on _movementBonus so the
    // existing engine reach computation honors it; revert queue
    // unwinds at _stepPruneExpiredEffects.
    card._movementBonus = (card._movementBonus || 0) + delta;
    host._broadcastCardStat(card.id, 'mov', card._movementBonus);
    host._refreshMovementHUD(card.id);
    if (!host._thisTurnMovReverts) host._thisTurnMovReverts = [];
    host._thisTurnMovReverts.push({ cardId: card.id, delta });
    return;
  }
  // "+N power" raises ATK and DEF (life) together. In Sorcery any card
  // that grants power buffs both equally. `stat: 'atk'` (explicit) buffs
  // attack only. The revert (turnPhaseRunner) reads `stat` to undo both.
  const buffsLife = stat === 'power';
  card.currentAttack = (card.currentAttack ?? card.baseAttack ?? 0) + delta;
  host._broadcastCardStat(card.id, 'atk', card.currentAttack);
  if (buffsLife) {
    card.currentLife = (card.currentLife ?? card.baseLife ?? 0) + delta;
    host._broadcastCardStat(card.id, 'hp', card.currentLife);
  }
  if (!host._thisTurnStatReverts) host._thisTurnStatReverts = [];
  host._thisTurnStatReverts.push({ cardId: card.id, delta, stat });
  const hud = host.lifeHUDs.get(card.id);
  if (hud?.sprite) {
    const display = card.currentAttack + (card._auraStatDelta || 0);
    updateLifeHUD(hud.sprite, display, 'atk', display - (card.baseAttack ?? display));
  }
  if (buffsLife && hud?.hpSprite) {
    const display = card.currentLife + (card._auraStatDelta || 0);
    updateLifeHUD(hud.hpSprite, display, 'hp', display - (card.baseLife ?? display));
  }
}

// v1.37. Single tap orchestrator. Every code path that flips a
// card's tapped state funnels through here: F hotkey (single +
// bulk), action menu Tap/Untap, double-click ability activation
// tap-cost, auto-tap on move, end-of-turn untap, network sync
// receiver, and any future card effect. Centralises the rule check
// (canTap / canUntap from tap.js) AND the side-effect bundle
// (animation, physics body rotation, broadcast, sound, log) so
// policy changes happen in exactly one place.
//
// Returns true iff the card's tapped state actually changed.
//
// `source` documents the call site and gates a few behaviours:
//   'remote'. Sync receiver. Authoritative; skips the rule
//                check (the peer already enforced its policy) and
//                must not re-broadcast.
//   'end_turn'. Bulk untap. Caller suppresses log + sound + writes
//                its own aggregate log if any.
//   'move'. Auto-tap during movement commit. Caller already
//                logged "Moved X from [A] to [B]"; suppress the
//                tap log to avoid duplication.
//   any other. Normal interactive flow with animation, broadcast,
//                and per-card log entry by default.
export function applyTap(host, card, mesh, nextValue, {
  source = 'unknown',
  animate = true,
  broadcast = true,
  sound = false,
  log = true,
} = {}) {
  if (!card || !mesh) return false;
  if (card.tapped === nextValue) return false;
  if (source !== 'remote') {
    if (nextValue === true  && !canTap(card))   return false;
    if (nextValue === false && !card.tapped)    return false; // already untapped, no canUntap rule to check
    // v1.54. Summoning sickness blocks cost-paying taps
    // (activate / move / attack). Effect-driven taps and user-
    // override taps (action_menu, hotkey) are unaffected. Codex
    // "A minion with summoning sickness cannot tap or be tapped
    // to pay costs (but may be tapped directly by other effects)."
    if (nextValue === true && blocksCostTap(card, source)) {
      if (source === 'activate' || source === 'attack') {
        toast.message(t('{name} has summoning sickness.', { name: card.name || t('Card') }));
      }
      return false;
    }
  }
  card.tapped = nextValue;
  if (animate) animateCardTap(mesh, card);
  // Sites start landscape; XOR with isSite keeps the body footprint
  // matching the rendered orientation for collision purposes.
  setCardBodyLandscape(mesh.userData?.body, Boolean(card.isSite) !== Boolean(card.tapped));
  if (broadcast) host._broadcastTap(card.id, card.tapped);
  if (sound) playSound('cardPlace');
  // Only the actor logs. Receiver-side _applyTap (source==='remote')
  // is pure state mutation. The logbook:entry channel carries the
  // canonical line so both clients see identical text.
  if (log && source !== 'remote') {
    if (nextValue) host.logAction('Tapped {card}', { card: cardLogLabel(card) });
    else host.logAction('Untapped {card}', { card: cardLogLabel(card) });
  }
  // on_tap watchers (False Idol: "Whenever a nearby minion taps, its
  // opponent draws a spell"). Fire only on the acting client for a
  // real tap of a minion. Remote-applied taps are the synced mirror,
  // and the watcher's draw funnels through host.drawCard which
  // re-broadcasts the affected pile, so firing on both clients would
  // double-draw.
  if (nextValue === true && source !== 'remote' && countsAsMinion(card)) {
    host._fireOnTap?.(card);
  }
  return true;
}
